/**
 * Converts camelCase or snake_case to Title Case With Spaces
 * Example: "employeeStatusId" → "Employee Status Id"
 * @param key - The key to format
 * @param removeIdSuffix - If true, removes "Id" or "Ids" suffix from the label
 */
export const formatKeyToLabel = (key: string, removeIdSuffix: boolean = false): string => {
  // Remove Id/Ids suffix if option is enabled
  let processedKey = key;
  if (removeIdSuffix) {
    processedKey = processedKey.replace(/Ids?$/, '');
  }

  // Replace underscores with spaces if any
  let result = processedKey.replace(/_/g, ' ');

  // Add space before capital letters and capitalize first letter
  result = result.replace(/([A-Z])/g, ' $1').trim();

  // Capitalize first letter
  result = result.charAt(0).toUpperCase() + result.slice(1);

  return result;
};

/**
 * Options for formatting key-value pairs
 */
export interface FormattedKeyValueOptions {
  /** Remove "Id" or "Ids" suffix from keys */
  removeIdSuffix?: boolean;
  /** Only include these keys (if provided) */
  includeKeys?: string[];
  /** Exclude these keys */
  excludeKeys?: string[];
}

/**
 * Transforms an object into a list of {key, value} pairs with formatted keys
 * Example: { employeeStatusId: "Approved" } → [{ key: "Employee Status Id", value: "Approved" }]
 * @param object - The object to transform
 * @param options - Options to control the transformation
 */
export const getFormattedKeyValuePairs = (
  object: Record<string, any>,
  options: FormattedKeyValueOptions = {}
): Array<{ key: string; value: any }> => {
  const { removeIdSuffix = false, includeKeys, excludeKeys = [] } = options;

  return Object.entries(object)
    .filter(([key]) => {
      // Filter by includeKeys if provided
      if (includeKeys?.length) {
        return includeKeys.includes(key);
      }

      // Filter out excludeKeys
      return !excludeKeys.includes(key);
    })
    .map(([key, value]) => ({
      key: formatKeyToLabel(key, removeIdSuffix),
      value,
    }));
};
